import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CantieriActions } from './cantieri.actions';
import { CantieriPort } from '../../core/services/cantieri/cantieri.port';
import { catchError, map, of, switchMap, withLatestFrom, tap, finalize } from 'rxjs';
import { selectLastQuery, selectPageSize } from './cantieri.selectors';
import { LoadingService } from '../../core/services/loading/loading.service';
import { Cantiere } from '../../core/models/cantieri.model';
import { SwalService } from '../../core/services/alerts/swal.service';
import { Router } from '@angular/router';

@Injectable()
export class CantieriEffects {
  // Stream globale di tutte le actions dispatchate nell'app.
  private actions$ = inject(Actions);
  // Porta API cantieri (astrazione service/backend).
  private api = inject(CantieriPort);
  private store = inject(Store);
  // Loading globale UI (overlay/spinner app).
  private loading = inject(LoadingService);
  // Servizio alert utente.
  private swalService = inject(SwalService);
  private router = inject(Router);

  // Carica la lista cantieri con fallback su page/size correnti dello store.
  loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.loadList),
      withLatestFrom(this.store.select(selectPageSize)),
      switchMap(([{ params }, pageSize]) => {
        const queryParams = {
          ...params,
          size: params?.size ?? pageSize,
          page: params?.page ?? 0,
        };

        return this.api.getList(queryParams).pipe(
          map((response) =>
            CantieriActions.loadListSuccess({
              items: response.content,
              totalElements: response.totalElements,
              totalPages: response.totalPages,
              pageIndex: response.pageIndex,
            })
          ),
          catchError((err) =>
            of(CantieriActions.loadListFailure({ error: err?.message ?? 'Errore caricamento' }))
          )
        );
      })
    )
  );

  // Carica il dettaglio singolo cantiere.
  loadDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.loadDetails),
      tap(() => this.loading.show()),
      switchMap(({ id }) =>
        this.api.getById(id).pipe(
          map((item) => CantieriActions.loadDetailsSuccess({ item })),
          catchError((err) =>
            of(CantieriActions.loadDetailsFailure({ error: err?.message ?? 'Errore dettagli' }))
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  // Crea un nuovo cantiere.
  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.create),
      tap(() => this.loading.show()),
      switchMap(({ dto, contratto  }) =>
        this.api.create(dto, contratto ).pipe(
          map((id) => CantieriActions.createSuccess({ id })),
          catchError((err) =>
            of(CantieriActions.createFailure({ error: err?.message ?? 'Errore creazione' }))
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  // Aggiorna dati cantiere.
  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.update),
      tap(() => this.loading.show()),
      switchMap(({ id, changes }) =>
        this.api.update(id, changes).pipe(
          map((returnedId) => CantieriActions.updateSuccess({
            id: Number(returnedId)
          })),
          catchError((err) =>
            of(CantieriActions.updateFailure({ error: err?.message ?? 'Errore modifica' }))
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  // Elimina un cantiere.
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.delete),
      tap(() => this.loading.show()),
      switchMap(({ id }) =>
        this.api.delete(id).pipe(
          map(() => CantieriActions.deleteSuccess({ id })),
          catchError((err) =>
            of(CantieriActions.deleteFailure({ error: err?.message ?? 'Errore eliminazione' }))
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  // Sposta cantiere in archivio.
  archive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.archive),
      tap(() => this.loading.show()),
      switchMap(({ id }) =>
        this.api.archive(id).pipe(
          map((resId) => CantieriActions.archiveSuccess({ id: Number(resId ?? id) })),
          catchError((err) =>
            of(CantieriActions.archiveFailure({ error: err?.message ?? 'Errore archivio' }))
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  // Ripristina cantiere da archivio.
  unarchive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.unarchive),
      tap(() => this.loading.show()),
      switchMap(({ id }) =>
        this.api.unarchive(id).pipe(
          map((resId) => CantieriActions.unarchiveSuccess({ id: Number(resId ?? id) })),
          catchError((err) =>
            of(CantieriActions.unarchiveFailure({ error: err?.message ?? 'Errore ripristino' }))
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  // Conclude cantiere impostando data consegna.
  end$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.end),
      tap(() => this.loading.show()),
      switchMap(({ id, dataConsegna }) =>
        this.api.end(id, dataConsegna).pipe(
          map((resId) => CantieriActions.endSuccess({ id: Number(resId ?? id) })),
          catchError((err) =>
            of(CantieriActions.endFailure({ error: err?.message ?? 'Errore conclusione' }))
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  // Associa/disassocia responsabile dipendente.
  associateDipendente$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.associateDipendente),
      tap(() => this.loading.show()),
      switchMap(({ id, idDipendente, associate }) =>
        this.api.associateDipendente(id, idDipendente, associate).pipe(
          map((resId) => CantieriActions.associateDipendenteSuccess({ id: Number(resId ?? id) })),
          catchError((err) =>
            of(CantieriActions.associateDipendenteFailure({ error: err?.message ?? 'Errore assegnazione responsabile' }))
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  // Associa/disassocia fornitore al cantiere.
  associateFornitore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.associateFornitore),
      tap(() => this.loading.show()),
      switchMap(({ id, idFornitore, associate }) =>
        this.api.associateFornitore(id, idFornitore, associate).pipe(
          map((resId) => CantieriActions.associateFornitoreSuccess({ id: Number(resId ?? id), idFornitore })),
          catchError((err) =>
            of(CantieriActions.associateFornitoreFailure({ error: err?.message ?? 'Errore associazione fornitore' }))
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  // Crea cantiere a partire da un preventivo.
  createFromPreventivo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.createFromPreventivo),
      tap(() => this.loading.show()),
      switchMap(({ dto }) =>
        this.api.createFromPreventivo(dto).pipe(
          map((response) => {
            // Il backend ritorna solo l'ID (number), trasformalo in oggetto
            const cantiereId = typeof response === 'number' ? response : (response as any)?.id;
            return CantieriActions.createFromPreventivoSuccess({
              item: { id: cantiereId } as Cantiere
            });
          }),
          catchError((err) =>
            of(CantieriActions.createFromPreventivoFailure({
              error: err?.message ?? 'Errore conversione in cantiere'
            }))
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  // Dopo una mutation riuscita sulla lista, rilancia loadList con l'ultima query usata.
  refreshListAfterMutations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CantieriActions.createSuccess,
        CantieriActions.updateSuccess,
        CantieriActions.deleteSuccess,
        CantieriActions.archiveSuccess,
        CantieriActions.unarchiveSuccess,
        CantieriActions.endSuccess
      ),
      withLatestFrom(this.store.select(selectLastQuery)),
      map(([_, lastQuery]) => CantieriActions.loadList({ params: lastQuery }))
    )
  );

  // Dopo mutation sul dettaglio, ricarica sempre il dettaglio aggiornato.
  refreshDetailsAfterMutations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CantieriActions.updateSuccess,
        CantieriActions.endSuccess,
        CantieriActions.associateDipendenteSuccess,
        CantieriActions.associateFornitoreSuccess
      ),
      map(({ id }) => CantieriActions.loadDetails({ id }))
    )
  );

  // Toast di successo centralizzati (invece di gestirli nei componenti).
  showMutationSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CantieriActions.createSuccess,
          CantieriActions.updateSuccess,
          CantieriActions.deleteSuccess,
          CantieriActions.archiveSuccess,
          CantieriActions.unarchiveSuccess,
          CantieriActions.endSuccess,
          CantieriActions.associateDipendenteSuccess,
          CantieriActions.associateFornitoreSuccess,
          CantieriActions.createFromPreventivoSuccess
        ),
        tap((action) => {
          switch (action.type) {
            case CantieriActions.createSuccess.type:
              this.swalService.success('Cantiere creato con successo!');
              return;
            case CantieriActions.updateSuccess.type:
              this.swalService.success('Cantiere aggiornato con successo!');
              return;
            case CantieriActions.deleteSuccess.type:
              this.swalService.success('Cantiere eliminato con successo!');
              return;
            case CantieriActions.archiveSuccess.type:
              this.swalService.success('Cantiere spostato in archivio');
              return;
            case CantieriActions.unarchiveSuccess.type:
              this.swalService.success('Cantiere ripristinato');
              return;
            case CantieriActions.endSuccess.type:
              this.swalService.success('Cantiere concluso con successo!');
              return;
            case CantieriActions.associateDipendenteSuccess.type:
              this.swalService.success('Responsabile aggiornato con successo!');
              return;
            case CantieriActions.associateFornitoreSuccess.type:
              this.swalService.success('Fornitore associato con successo!');
              return;
            case CantieriActions.createFromPreventivoSuccess.type:
              this.swalService.success('Cantiere creato con successo!');
              return;
          }
        })
      ),
    { dispatch: false }
  );

  // Toast di errore centralizzati per le mutation fallite.
  showMutationFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CantieriActions.createFailure,
          CantieriActions.updateFailure,
          CantieriActions.deleteFailure,
          CantieriActions.archiveFailure,
          CantieriActions.unarchiveFailure,
          CantieriActions.endFailure,
          CantieriActions.associateDipendenteFailure,
          CantieriActions.associateFornitoreFailure,
          CantieriActions.createFromPreventivoFailure
        ),
        tap((action) => {
          if (action.type === CantieriActions.associateFornitoreFailure.type) {
            const normalizedError = (action.error ?? '').toLowerCase();
            const alreadyAssociated = normalizedError.includes('gia')
              || normalizedError.includes('already')
              || normalizedError.includes('associat');
            if (alreadyAssociated) {
              this.swalService.notify('E gia associato al cantiere selezionato', 'info');
              return;
            }
          }
          this.swalService.error(`Errore: ${action.error}`);
        })
      ),
    { dispatch: false }
  );

  navigateOnCreateFromPreventivoSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CantieriActions.createFromPreventivoSuccess),
        tap(({ item }) => this.router.navigate(['/cantieri', item.id]))
      ),
    { dispatch: false }
  );

  loadContratti$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.loadContratti),
      switchMap(({ idCantiere }) =>
        this.api.getContrattiByCantiere(idCantiere).pipe(
          map(items => CantieriActions.loadContrattiSuccess({ items })),
          catchError(err =>
            of(CantieriActions.loadContrattiFailure({ error: err?.message ?? 'Errore caricamento allegati' }))
          )
        )
      )
    )
  );

  createContratto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.createContratto),
      switchMap(({ idCantiere, file, descrizione }) =>
        this.api.createContrattoCantiere(idCantiere, file, descrizione).pipe(
          map(id => CantieriActions.createContrattoSuccess({ idCantiere, id })),
          catchError(err =>
            of(CantieriActions.createContrattoFailure({ error: err?.message ?? 'Errore creazione allegato' }))
          )
        )
      )
    )
  );

  reloadAfterCreateContratto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.createContrattoSuccess),
      map(({ idCantiere }) => CantieriActions.loadContratti({ idCantiere }))
    )
  );

  updateContratto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.updateContratto),
      switchMap(({ id, idCantiere, file, descrizione }) =>
        this.api.updateContrattoCantiere(id, file, descrizione).pipe(
          map(() => CantieriActions.updateContrattoSuccess({ idCantiere, id })),
          catchError(err =>
            of(CantieriActions.updateContrattoFailure({ error: err?.message ?? 'Errore modifica allegato' }))
          )
        )
      )
    )
  );

  reloadAfterUpdateContratto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.updateContrattoSuccess),
      map(({ idCantiere }) => CantieriActions.loadContratti({ idCantiere }))
    )
  );

  deleteContratto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.deleteContratto),
      switchMap(({ id, idCantiere }) =>
        this.api.deleteContrattoCantiere(id).pipe(
          map(() => CantieriActions.deleteContrattoSuccess({ idCantiere, id })),
          catchError(err =>
            of(CantieriActions.deleteContrattoFailure({ error: err?.message ?? 'Errore eliminazione allegato' }))
          )
        )
      )
    )
  );

  reloadAfterDeleteContratto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.deleteContrattoSuccess),
      map(({ idCantiere }) => CantieriActions.loadContratti({ idCantiere }))
    )
  );

  previewFile$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CantieriActions.previewFile),
        switchMap(({ fileId }) =>
          this.api.previewFile(fileId).pipe(
            tap(blob => {
              const url = URL.createObjectURL(blob);
              window.open(url, '_blank');
            }),
            catchError(err =>
              of(CantieriActions.previewFileFailure({ error: err?.message ?? 'Errore apertura file' }))
            )
          )
        )
      ),
    { dispatch: true }
  );

  loadPieStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.loadPieStats),
      switchMap(({ idCantiere }) =>
        this.api.getPieByIdCantiere(idCantiere).pipe(
          map(data => CantieriActions.loadPieStatsSuccess({ data })),
          catchError(error =>
            of(CantieriActions.loadPieStatsFailure({ error: error?.message ?? 'Errore caricamento grafico torta' }))
          )
        )
      )
    )
  );

  loadFlussoMensileStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.loadFlussoMensileStats),
      switchMap(({ idCantiere }) =>
        this.api.getFlussoMensileByIdCantiere(idCantiere).pipe(
          map(data => CantieriActions.loadFlussoMensileStatsSuccess({ data })),
          catchError(error =>
            of(CantieriActions.loadFlussoMensileStatsFailure({ error: error?.message ?? 'Errore caricamento flusso mensile' }))
          )
        )
      )
    )
  );
}
